// contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { sendEmailOTP, verifyEmailOTP, getCurrentUser, logoutUser } from '../config/appwrite';
import { dbSet, dbGet, getSafeUserId, cleanFirebaseData, dbOnValue, dbUpdate } from '../config/firebase';
import { toast } from 'react-toastify';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

// Demo credentials for admin/staff (password login only – NOT for customers)
const DEMO_USERS = {
  'admin@sugandhi.com': { password: 'admin123', role: 'admin', name: 'Admin User' },
  'staff@sugandhi.com': { password: 'staff123', role: 'staff', name: 'Staff User' }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [allUsers, setAllUsers] = useState([]);

  // Load all users from Firebase (for staff/admin password login)
  useEffect(() => {
    const unsubscribe = dbOnValue('users', (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const usersList = Object.entries(data).map(([key, userData]) => ({
          firebaseKey: key,
          ...userData
        }));
        setAllUsers(usersList);
      }
    });
    return () => unsubscribe();
  }, []);

  // Restore user from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('sugandhi_user');
    if (saved) {
      try {
        setUser(JSON.parse(saved));
      } catch {
        localStorage.removeItem('sugandhi_user');
      }
    }
    setLoading(false);
  }, []);

  // Request OTP (only for customers – no demo fallback)
  const requestOTP = useCallback(async (email) => {
    // Admin/staff should use password login, not OTP
    if (DEMO_USERS[email]) {
      toast.error('Please use password login for admin/staff accounts');
      throw new Error('Use password login for admin/staff accounts');
    }
    try {
      const result = await sendEmailOTP(email);
      setUserId(result.userId);
      return result.userId;
    } catch (error) {
      console.error('OTP send error:', error);
      throw new Error('Failed to send OTP. Please check your email and try again.');
    }
  }, []);

  // Login with password (for admin/staff)
  const loginWithPassword = useCallback(async (email, password) => {
    // Check demo users
    const demoUser = DEMO_USERS[email];
    if (demoUser && demoUser.password === password) {
      const userData = {
        $id: 'demo_' + email,
        email,
        name: demoUser.name,
        role: demoUser.role,
        phone: '',
        address: '',
        createdAt: new Date().toISOString()
      };
      setUser(userData);
      localStorage.setItem('sugandhi_user', JSON.stringify(userData));
      return true;
    }
    // Check Firebase users (staff created by admin)
    const firebaseUser = allUsers.find(u => u.email === email && u.password === password);
    if (firebaseUser) {
      const userData = {
        $id: firebaseUser.firebaseKey,
        email: firebaseUser.email,
        name: firebaseUser.name,
        role: firebaseUser.role || 'staff',
        phone: firebaseUser.phone || '',
        address: firebaseUser.address || '',
        status: firebaseUser.status || 'active',
        createdAt: firebaseUser.createdAt || new Date().toISOString()
      };
      if (userData.status !== 'active') {
        throw new Error('Your account is inactive. Please contact administrator.');
      }
      setUser(userData);
      localStorage.setItem('sugandhi_user', JSON.stringify(userData));
      return true;
    }
    throw new Error('Invalid email or password');
  }, [allUsers]);

  // Verify OTP – NO FALLBACK to demo user. Registration data (name, phone) is saved.
  const verifyOTP = useCallback(async (secret, registrationData = {}) => {
    if (!userId) {
      throw new Error('No OTP request found. Please request OTP first.');
    }
    try {
      await verifyEmailOTP(userId, secret);
      const currentUser = await getCurrentUser();
      const safeUserId = getSafeUserId(currentUser);
      
      let existingUser = await dbGet(`users/${safeUserId}`);
      if (!existingUser) {
        // Create new user with registration data
        const newUserData = cleanFirebaseData({
          email: currentUser.email,
          name: registrationData.name || currentUser.email.split('@')[0],
          phone: registrationData.phone || '',
          address: '',
          role: 'customer',
          createdAt: new Date().toISOString()
        });
        await dbSet(`users/${safeUserId}`, newUserData);
        existingUser = newUserData;
      } else if (registrationData.name || registrationData.phone) {
        // Update if missing fields
        const updates = {};
        if (registrationData.name && !existingUser.name) updates.name = registrationData.name;
        if (registrationData.phone && !existingUser.phone) updates.phone = registrationData.phone;
        if (Object.keys(updates).length > 0) {
          await dbUpdate(`users/${safeUserId}`, cleanFirebaseData(updates));
          existingUser = { ...existingUser, ...updates };
        }
      }
      
      const fullUser = { ...currentUser, ...existingUser };
      setUser(fullUser);
      localStorage.setItem('sugandhi_user', JSON.stringify(fullUser));
      setUserId(null);
      return true;
    } catch (error) {
      console.error('OTP verification failed:', error);
      throw new Error('Invalid OTP. Please try again.');
    }
  }, [userId]);

  const logout = useCallback(async () => {
    try {
      await logoutUser();
    } catch (e) {}
    localStorage.removeItem('sugandhi_user');
    setUser(null);
  }, []);

  const updateProfile = async (data) => {
    if (!user) return;
    const safeUserId = getSafeUserId(user);
    const cleanData = cleanFirebaseData({
      ...user,
      ...data,
      updatedAt: new Date().toISOString()
    });
    await dbSet(`users/${safeUserId}`, cleanData);
    const updated = { ...user, ...data };
    setUser(updated);
    localStorage.setItem('sugandhi_user', JSON.stringify(updated));
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      userId,
      requestOTP,
      verifyOTP,
      loginWithPassword,
      logout,
      updateProfile,
      isAdmin: user?.role === 'admin',
      isStaff: user?.role === 'staff'
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};